import React, { useCallback, useId, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Copy, RefreshCw, Sparkles, Square, X } from 'lucide-react';
import {
  Avatar,
  Button,
  Dialog,
  DialogBody,
  DialogHeader,
  IconButton,
  Pill,
  SegmentedControl,
} from '../../../components/ui';
import { AiNotice, StreamProgress, plainText, useCopy } from '../../intelligence/components/BriefPanel';
import { IntelText } from '../../intelligence/components/IntelText';
import type { StreamHandlers } from '../../intelligence/intelligenceApi';
import { useIntelligenceStream } from '../../intelligence/useIntelligenceStream';
import { assessIssue, briefIssue, issuesErrorMessage, recordStance } from '../issuesApi';
import { useStances } from '../useIssues';
import type { IssueView } from '../../../types/issues';
import { CONFIDENCE_LABEL, subjectHref, subjectKey } from './format';
import { PositionChange, PositionPill } from './PositionMark';
import { StanceProposal, parseProposals } from './proposals';

type Mode = 'brief' | 'positions';

const MODES: { value: Mode; label: string }[] = [
  { value: 'brief', label: 'Assessment' },
  { value: 'positions', label: 'Assess positions' },
];

/**
 * The AI view of an issue: a written assessment of where it stands, and
 * proposed positions for actors nobody has placed yet. A proposal is only a
 * proposal — nothing reaches the record until an analyst accepts it, and what
 * is then written is marked as coming from AI.
 */
export const IssueBriefDialog: React.FC<{
  issue: IssueView;
  onClose: () => void;
  /** Called after a proposal is accepted, so the workspace reloads */
  onApplied: () => void;
}> = ({ issue, onClose, onApplied }) => {
  const titleId = useId();
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>('brief');

  const briefRunner = useCallback((id: string, handlers: StreamHandlers) => briefIssue(id, handlers), []);
  const assessRunner = useCallback((id: string, handlers: StreamHandlers) => assessIssue(id, handlers), []);
  const brief = useIntelligenceStream<string, any>(briefRunner);
  const assess = useIntelligenceStream<string, any>(assessRunner);

  const { stances } = useStances(issue.id);
  const knownSubjects = useMemo(() => {
    const map = new Map<string, { type: string; id: string }>();
    stances.forEach((stance) => map.set(stance.subject.id, stance.subject));
    return map;
  }, [stances]);
  const held = useMemo(() => {
    const map = new Map<string, number>();
    stances.forEach((stance) => map.set(subjectKey(stance.subject), stance.position));
    return map;
  }, [stances]);

  const stream = mode === 'brief' ? brief : assess;
  const running = stream.status === 'running';
  const failed = stream.status === 'error' || stream.status === 'unavailable';
  const { copied, copy } = useCopy();

  const [dismissed, setDismissed] = useState<string[]>([]);
  const [accepted, setAccepted] = useState<string[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const proposals = useMemo(() => parseProposals(assess.result), [assess.result]);
  const open = proposals.filter((p) => !dismissed.includes(p.key) && !accepted.includes(p.key));

  const accept = async (proposal: StanceProposal) => {
    setBusy(proposal.key);
    setActionError(null);
    try {
      await recordStance(issue.id, {
        subjectType: proposal.subject.type,
        subjectId: proposal.subject.id,
        position: proposal.position,
        confidence: proposal.confidence,
        source: 'ai',
        rationale: proposal.rationale,
        evidenceUrl: proposal.evidenceUrl,
        ...(proposal.issueInfluence !== undefined ? { issueInfluence: proposal.issueInfluence } : {}),
      });
      setAccepted((list) => [...list, proposal.key]);
      onApplied();
    } catch (err) {
      setActionError(issuesErrorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const onEntity = (id: string) => {
    const subject = knownSubjects.get(id);
    if (!subject) return;
    onClose();
    navigate(subjectHref(subject));
  };

  return (
    <Dialog open onClose={onClose} labelledBy={titleId} handle className="max-h-[90dvh] sm:max-w-2xl">
      <DialogHeader id={titleId} eyebrow={issue.title} title="AI assessment" onClose={onClose} />
      <DialogBody className="pb-6">
        <SegmentedControl label="Assessment mode" items={MODES} value={mode} onChange={setMode} className="mb-3" />

        {/* ---- generate ---- */}
        {!running && !stream.text && (
          <div className="flex flex-col items-start gap-2">
            <Button size="sm" variant="secondary" onClick={() => stream.start(issue.id)}>
              <Sparkles className="h-3.5 w-3.5 text-primary-600" strokeWidth={1.75} />
              {mode === 'brief' ? 'Write the assessment' : 'Propose positions'}
            </Button>
            <p className="text-[12px] text-neutral-400">
              {mode === 'brief'
                ? 'Balance of forces, coalitions and who to move first'
                : 'Proposes a position for actors with none — nothing is recorded until you accept it'}
            </p>
          </div>
        )}

        {running && !stream.text && <StreamProgress lines={stream.progress} running className="mt-1" />}

        {!!stream.text && (
          <>
            <div className="mb-2 flex min-h-[28px] items-center gap-1">
              <p className="min-w-0 flex-1 truncate text-[12px] text-neutral-400">
                {running ? stream.progress[stream.progress.length - 1] || 'Writing' : 'Just now'}
              </p>
              {running ? (
                <IconButton label="Stop" size="xs" onClick={stream.stop}>
                  <Square className="h-3 w-3 fill-current" strokeWidth={1.75} />
                </IconButton>
              ) : (
                <>
                  <IconButton label={copied ? 'Copied' : 'Copy'} size="xs" onClick={() => copy(plainText(stream.text))}>
                    {copied ? <Check className="h-3.5 w-3.5" strokeWidth={2} /> : <Copy className="h-3.5 w-3.5" strokeWidth={1.75} />}
                  </IconButton>
                  <IconButton label="Regenerate" size="xs" onClick={() => stream.start(issue.id)}>
                    <RefreshCw className="h-3.5 w-3.5" strokeWidth={1.75} />
                  </IconButton>
                </>
              )}
            </div>
            <IntelText text={stream.text} streaming={running} onEntity={onEntity} />
          </>
        )}

        {failed && <AiNotice status={stream.status} error={stream.error} onRetry={() => stream.start(issue.id)} className="mt-2" />}

        {/* ---- proposals ---- */}
        {mode === 'positions' && (
          <div className="mt-4">
            {actionError && (
              <p role="alert" className="mb-2 rounded-xl bg-primary-50 px-3.5 py-2.5 text-[13px] text-primary-700">
                {actionError}
              </p>
            )}

            {accepted.length > 0 && (
              <p role="status" className="mb-2 flex items-center gap-1.5 text-[12px] text-emerald-700">
                <Check className="h-3.5 w-3.5" strokeWidth={2} />
                {accepted.length} position{accepted.length === 1 ? '' : 's'} recorded
              </p>
            )}

            {assess.status === 'done' && !proposals.length && !running && (
              <p className="text-[12px] text-neutral-400">No positions proposed</p>
            )}

            {open.length > 0 && (
              <>
                <h3 className="mb-2 text-[12px] uppercase tracking-[0.06em] text-neutral-400">
                  Proposed positions · {open.length}
                </h3>
                <ul className="space-y-2">
                  {open.map((proposal) => {
                    const current = held.get(proposal.key);
                    return (
                      <li key={proposal.key} className="rounded-2xl border border-neutral-200 p-3">
                        <div className="flex items-start gap-2.5">
                          <Avatar name={proposal.subject.label} kind={proposal.subject.type} size="sm" className="mt-0.5" />
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-[13px] text-ink">{proposal.subject.label}</p>
                            <div className="mt-1 flex flex-wrap items-center gap-2">
                              {current === undefined ? (
                                <PositionPill position={proposal.position} short />
                              ) : (
                                <PositionChange from={current} to={proposal.position} />
                              )}
                              <Pill tone="neutral" title="How sure the model says it is">
                                {CONFIDENCE_LABEL[proposal.confidence]} confidence
                              </Pill>
                            </div>
                          </div>
                        </div>
                        {proposal.rationale && (
                          <p className="mt-2 text-[12.5px] leading-snug text-neutral-600">{proposal.rationale}</p>
                        )}
                        {proposal.evidenceUrl && (
                          <a
                            href={proposal.evidenceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-1.5 inline-block truncate text-[11.5px] text-neutral-500 underline decoration-neutral-300 underline-offset-2 hover:text-ink"
                          >
                            {proposal.evidenceUrl}
                          </a>
                        )}
                        <div className="mt-2.5 flex items-center justify-end gap-2">
                          <Button
                            size="xs"
                            variant="ghost"
                            onClick={() => setDismissed((list) => [...list, proposal.key])}
                            disabled={busy === proposal.key}
                          >
                            <X className="h-3.5 w-3.5" strokeWidth={1.75} />
                            Dismiss
                          </Button>
                          <Button
                            size="xs"
                            variant="dark"
                            loading={busy === proposal.key}
                            onClick={() => accept(proposal)}
                          >
                            <Check className="h-3.5 w-3.5" strokeWidth={2} />
                            Accept
                          </Button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
                <p className="mt-2 text-[11.5px] text-neutral-400">
                  Accepting records the position with AI as its source; the history keeps who accepted it.
                </p>
              </>
            )}
          </div>
        )}
      </DialogBody>
    </Dialog>
  );
};

export default IssueBriefDialog;
